import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowUpRight, Sparkles } from 'lucide-react';
import { cn } from '../../utils/cn';
import { solutions } from '../../data/solutions';

const navItems = [
  { name: 'Início', path: '/' },
  { name: 'Serviços', path: '/servicos' },
  { name: 'Sobre Nós', path: '/sobre' },
  { name: 'Soluções', path: '/solucoes', hasMegaMenu: true },
  { name: 'Projectos', path: '/projectos' },
  { name: 'Contactos', path: '/contactos' },
];

export const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fechar menu mobile na navegação
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-500',
        scrolled
          ? 'py-3.5 bg-[#08090C]/90 backdrop-blur-xl border-b border-white/[0.07] shadow-[0_4px_30px_rgba(0,0,0,0.6)]'
          : 'py-6 bg-transparent'
      )}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-8 flex items-center justify-between">
        {/* Brand Logo com logo oficial */}
        <NavLink
          to="/"
          className="flex items-center gap-3 group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f15c12] rounded-lg"
          aria-label="Hiperbite - Página Inicial"
        >
          <div className="relative flex items-center justify-center">
            <img
              src="/assets/images/logo/ico_orange.svg"
              alt="Hiperbite Ícone"
              className="h-9 w-9 object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <div className="absolute inset-0 rounded-full bg-[#f15c12]/20 blur-md opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <div className="flex flex-col">
            <span className="font-heading font-extrabold text-2xl tracking-tight text-white flex items-center gap-1.5">
              Hiperbite
              <span className="text-[11px] font-mono font-bold px-1.5 py-0.5 rounded bg-[#f15c12]/15 text-[#f15c12] border border-[#f15c12]/30">
                AO
              </span>
            </span>
            <span className="text-[10px] tracking-widest uppercase font-mono text-zinc-400">
              Software & Cloud Solutions
            </span>
          </div>
        </NavLink>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1 rounded-full p-1.5 bg-white/[0.03] border border-white/[0.08] backdrop-blur-md">
          {navItems.map((item) => (
            item.hasMegaMenu ? (
              <div key={item.path} className="relative" onMouseEnter={() => setSolutionsOpen(true)} onMouseLeave={() => setSolutionsOpen(false)}>
                <NavLink to={item.path} className={({ isActive }) => cn('px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 relative flex items-center gap-2', isActive || solutionsOpen ? 'text-white' : 'text-zinc-400 hover:text-white hover:bg-white/[0.04]')}>
                  <span className="relative z-10">{item.name} <span className="text-[10px] text-[#f15c12]">08</span></span>
                  {(solutionsOpen || location.pathname.startsWith('/solucoes')) && <motion.div layoutId="nav-pill" className="absolute inset-0 rounded-full bg-[#f15c12]/15 border border-[#f15c12]/30" transition={{ type: 'spring', stiffness: 380, damping: 30 }} />}
                </NavLink>
                <AnimatePresence>
                  {solutionsOpen && <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className="absolute left-1/2 top-[calc(100%+16px)] z-50 w-[620px] -translate-x-1/2 border border-white/[0.14] bg-[#151516]/95 p-4 shadow-2xl backdrop-blur-2xl">
                    <div className="mb-3 flex items-center justify-between border-b border-white/[0.1] px-3 pb-3"><span className="eyebrow">Ecossistema Hiperbite</span><span className="font-mono text-[10px] text-white/35">08 soluções próprias</span></div>
                    <div className="grid grid-cols-2 gap-1">{solutions.map((solution) => { const Icon = solution.icon; return <NavLink key={solution.slug} to={`/solucoes/${solution.slug}`} className="group flex items-start gap-3 p-3 transition-colors hover:bg-white/[0.05]"><span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden border border-white/10 text-[#f15c12]">{solution.iconUrl ? <img src={solution.iconUrl} alt="" className="h-5 w-5 object-contain" /> : <Icon className="h-3.5 w-3.5" />}</span><span><strong className="block text-sm font-medium text-white">{solution.name}</strong><small className="mt-1 block text-[10px] leading-4 text-white/45">{solution.category}</small></span><ArrowUpRight className="ml-auto h-3.5 w-3.5 text-white/20 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#f15c12]" /></NavLink>; })}</div>
                  </motion.div>}
                </AnimatePresence>
              </div>
            ) : (
              <NavLink key={item.path} to={item.path} className={({ isActive }) => cn('px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 relative', isActive ? 'text-white' : 'text-zinc-400 hover:text-white hover:bg-white/[0.04]')}>
                {({ isActive }) => <><span className="relative z-10">{item.name}</span>{isActive && <motion.div layoutId="nav-pill" className="absolute inset-0 rounded-full bg-[#f15c12]/20 border border-[#f15c12]/40 shadow-sm" transition={{ type: 'spring', stiffness: 380, damping: 30 }} />}</>}
              </NavLink>
            )
          ))}
        </nav>

        {/* Right Action & Status */}
        <div className="hidden md:flex items-center gap-4">
          <div className=" hidden flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.02] border border-white/[0.08] text-xs text-zinc-300 font-mono">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#f15c12] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#f15c12]" />
            </span>
            <span>Disponível para novos projetos</span>
          </div>

          <NavLink
            to="/contactos"
            className="group relative inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-[#f15c12] hover:bg-[#ff6f26] shadow-[0_0_25px_rgba(241,92,18,0.4)] hover:shadow-[0_0_35px_rgba(241,92,18,0.6)] transition-all duration-300 hover:scale-[1.02]"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>Fale Connosco</span>
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </NavLink>
        </div>

        {/* Mobile Hamburger Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden p-2.5 rounded-xl bg-white/[0.05] border border-white/[0.08] text-white hover:bg-white/[0.1] focus:outline-none focus:ring-2 focus:ring-[#f15c12]"
          aria-label={isOpen ? 'Fechar Menu' : 'Abrir Menu'}
        >
          {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="md:hidden border-b border-white/[0.08] bg-[#08090C]/95 backdrop-blur-2xl px-6 py-6"
          >
            <div className="flex flex-col gap-3">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    cn(
                      'px-4 py-3 rounded-xl text-base font-medium transition-colors',
                      isActive
                        ? 'bg-[#f15c12]/15 text-[#f15c12] border border-[#f15c12]/30'
                        : 'text-zinc-300 hover:bg-white/[0.04] hover:text-white'
                    )
                  }
                >
                  {item.name}
                </NavLink>
              ))}

              <div className="pt-4 border-t border-white/[0.08] flex flex-col gap-3">
                <div className="flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-400 font-mono">
                  <span className="relative flex h-2 w-2">
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#f15c12]" />
                  </span>
                  <span>Disponível para novos projetos</span>
                </div>
                <NavLink
                  to="/contactos"
                  className="w-full text-center py-3 rounded-xl font-semibold text-white bg-[#f15c12] shadow-[0_0_20px_rgba(241,92,18,0.4)]"
                >
                  Fale Connosco
                </NavLink>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
